import React, { useEffect, useState } from 'react';
import { apiClient } from '../../config/api';

// Common actions and entity types for dropdowns
const COMMON_ACTIONS = [
  'CREATE', 'UPDATE', 'DELETE', 'LOGIN', 'LOGOUT', 'LOGIN_FAILED',
  'ACTIVATE', 'DEACTIVATE', 'EXECUTE', 'CANCEL', 'STATUS_CHANGE',
  'PASSWORD_CHANGE', 'PASSWORD_RESET_REQUEST', 'ROLE_ASSIGN', 'ROLE_REMOVE',
  'VARIABLE_MAPPING_UPDATE', 'BANNER_UPDATE', 'BRAND_ASSOCIATE', 'LOCATION_ADD',
  'DATA_IMPORT', 'DATA_EXPORT'
];

const COMMON_ENTITY_TYPES = [
  'user', 'role', 'oem', 'group', 'event', 'appointment', 'workflow',
  'client', 'job', 'invoice', 'import', 'export', 'authentication', 'user_role',
  'oem_brand', 'location', 'workflow'
];

// Action color mapping
const getActionColor = (action) => {
  const actionUpper = action.toUpperCase();
  if (actionUpper.includes('CREATE') || actionUpper.includes('LOGIN')) {
    return 'bg-green-100 text-green-800';
  } else if (actionUpper.includes('UPDATE') || actionUpper.includes('CHANGE') || actionUpper.includes('ASSIGN')) {
    return 'bg-blue-100 text-blue-800';
  } else if (actionUpper.includes('DELETE') || actionUpper.includes('REMOVE') || actionUpper.includes('CANCEL')) {
    return 'bg-red-100 text-red-800';
  } else if (actionUpper.includes('FAILED') || actionUpper.includes('ERROR')) {
    return 'bg-yellow-100 text-yellow-800';
  } else if (actionUpper.includes('ACTIVATE') || actionUpper.includes('EXECUTE')) {
    return 'bg-purple-100 text-purple-800';
  } else if (actionUpper.includes('DEACTIVATE')) {
    return 'bg-gray-100 text-gray-800';
  }
  return 'bg-gray-100 text-gray-800';
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filters, setFilters] = useState({
    entity_type: '',
    entity_id: '',
    user_id: '',
    action: '',
    start_date: '',
    end_date: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchLogs();
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [currentPage, filters]);

  const fetchUsers = async () => {
    try {
      const data = await apiClient.get('/users');
      setUsers(data.users || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '50',
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      if (searchQuery) {
        params.append('search', searchQuery);
      }

      const data = await apiClient.get(`/audit-logs?${params.toString()}`);
      setLogs(data.logs || []);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (err) {
      setError('Failed to load audit logs');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await apiClient.get('/audit-logs/stats/summary');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      entity_type: '',
      entity_id: '',
      user_id: '',
      action: '',
      start_date: '',
      end_date: '',
    });
    setSearchQuery('');
    setCurrentPage(1);
  };

  const applyDatePreset = (preset) => {
    const today = new Date();
    const endDate = today.toISOString().split('T')[0];
    let startDate = '';

    switch (preset) {
      case 'today':
        startDate = endDate;
        break;
      case '7days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);
        startDate = sevenDaysAgo.toISOString().split('T')[0];
        break;
      case '30days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(today.getDate() - 30);
        startDate = thirtyDaysAgo.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    setFilters({ ...filters, start_date: startDate, end_date: endDate });
    setCurrentPage(1);
  };

  const exportToCSV = () => {
    const headers = ['Timestamp', 'User', 'Action', 'Entity Type', 'Entity ID', 'IP Address', 'User Agent'];
    const rows = logs.map(log => [
      new Date(log.created_at).toISOString(),
      log.user_name || log.user_email || 'System',
      log.action,
      log.entity_type || '',
      log.entity_id || '',
      log.ip_address || '',
      log.user_agent || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToJSON = () => {
    const dataStr = JSON.stringify(logs, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatJson = (obj) => {
    if (!obj) return '-';
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  };

  const getDiff = (oldValues, newValues) => {
    if (!oldValues || !newValues) return null;
    
    const diff = {};
    const allKeys = new Set([...Object.keys(oldValues), ...Object.keys(newValues)]);
    
    allKeys.forEach(key => {
      const oldVal = oldValues[key];
      const newVal = newValues[key];
      
      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        diff[key] = { old: oldVal, new: newVal };
      }
    });
    
    return Object.keys(diff).length > 0 ? diff : null;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const openDetailModal = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  if (loading && logs.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading audit logs...</div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
          <p className="text-gray-600 mt-1">View system activity and changes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={exportToCSV}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Export CSV
          </button>
          <button
            onClick={exportToJSON}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Export JSON
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Total Logs</div>
            <div className="text-3xl font-bold text-gray-900">{stats.total_logs}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Unique Users</div>
            <div className="text-3xl font-bold text-blue-600">{stats.unique_users}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Entity Types</div>
            <div className="text-3xl font-bold text-purple-600">{stats.entity_types}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Last 7 Days</div>
            <div className="text-3xl font-bold text-green-600">{stats.logs_last_7_days}</div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="text-sm text-gray-600 mb-1">Last 30 Days</div>
            <div className="text-3xl font-bold text-orange-600">{stats.logs_last_30_days}</div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="mb-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search audit logs..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
          </form>
        </div>

        <h2 className="text-lg font-semibold mb-4">Filters</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              User
            </label>
            <select
              value={filters.user_id}
              onChange={(e) => handleFilterChange('user_id', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.first_name} {user.last_name} ({user.email})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Action Type
            </label>
            <select
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Actions</option>
              {COMMON_ACTIONS.map(action => (
                <option key={action} value={action}>{action}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entity Type
            </label>
            <select
              value={filters.entity_type}
              onChange={(e) => handleFilterChange('entity_type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Entity Types</option>
              {COMMON_ENTITY_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Entity ID
            </label>
            <input
              type="text"
              value={filters.entity_id}
              onChange={(e) => handleFilterChange('entity_id', e.target.value)}
              placeholder="UUID"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => applyDatePreset('today')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Today
          </button>
          <button
            onClick={() => applyDatePreset('7days')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Last 7 Days
          </button>
          <button
            onClick={() => applyDatePreset('30days')}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
          >
            Last 30 Days
          </button>
          <button
            onClick={clearFilters}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Audit Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Entity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Changes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No audit logs found
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const diff = getDiff(log.old_values, log.new_values);
                  return (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(log.created_at).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {log.user_name || 'System'}
                        </div>
                        <div className="text-sm text-gray-500">{log.user_email || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(log.action)}`}>
                          {log.action}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{log.entity_type}</div>
                        {log.entity_id && (
                          <div className="text-sm text-gray-500 font-mono text-xs">
                            {log.entity_id.substring(0, 8)}...
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {diff ? (
                          <div className="text-xs">
                            {Object.keys(diff).slice(0, 3).map(key => (
                              <div key={key} className="mb-1">
                                <span className="font-semibold">{key}:</span>
                                <span className="text-red-600 line-through ml-1">
                                  {JSON.stringify(diff[key].old).substring(0, 20)}
                                </span>
                                <span className="text-green-600 ml-1">
                                  → {JSON.stringify(diff[key].new).substring(0, 20)}
                                </span>
                              </div>
                            ))}
                            {Object.keys(diff).length > 3 && (
                              <div className="text-gray-500">+{Object.keys(diff).length - 3} more</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">No changes</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => openDetailModal(log)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t border-gray-200">
            <div className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Audit Log Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Timestamp</label>
                  <div className="text-sm text-gray-900">{new Date(selectedLog.created_at).toLocaleString()}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">User</label>
                  <div className="text-sm text-gray-900">
                    {selectedLog.user_name || selectedLog.user_email || 'System'}
                    {selectedLog.user_email && (
                      <div className="text-xs text-gray-500">{selectedLog.user_email}</div>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Action</label>
                  <div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getActionColor(selectedLog.action)}`}>
                      {selectedLog.action}
                    </span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Entity Type</label>
                  <div className="text-sm text-gray-900">{selectedLog.entity_type || '-'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Entity ID</label>
                  <div className="text-sm text-gray-900 font-mono">{selectedLog.entity_id || '-'}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">IP Address</label>
                  <div className="text-sm text-gray-900 font-mono">{selectedLog.ip_address || '-'}</div>
                </div>
              </div>
              {selectedLog.user_agent && (
                <div>
                  <label className="text-sm font-medium text-gray-500">User Agent</label>
                  <div className="text-sm text-gray-900 font-mono break-all">{selectedLog.user_agent}</div>
                </div>
              )}
              {selectedLog.old_values && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Old Values</label>
                  <pre className="bg-red-50 p-4 rounded-lg text-xs font-mono overflow-auto max-h-60">
                    {formatJson(selectedLog.old_values)}
                  </pre>
                </div>
              )}
              {selectedLog.new_values && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">New Values</label>
                  <pre className="bg-green-50 p-4 rounded-lg text-xs font-mono overflow-auto max-h-60">
                    {formatJson(selectedLog.new_values)}
                  </pre>
                </div>
              )}
              {selectedLog.old_values && selectedLog.new_values && (
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">Changes (Diff)</label>
                  <div className="bg-gray-50 p-4 rounded-lg text-xs font-mono overflow-auto max-h-60">
                    {Object.entries(getDiff(selectedLog.old_values, selectedLog.new_values) || {}).map(([key, { old, new: newVal }]) => (
                      <div key={key} className="mb-2">
                        <div className="font-semibold text-gray-900">{key}:</div>
                        <div className="text-red-600 line-through">{JSON.stringify(old)}</div>
                        <div className="text-green-600">→ {JSON.stringify(newVal)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
